import { useRef } from 'react';
import { Clock, Navigation, X, ChevronDown, Loader2, Bus, AlertTriangle } from 'lucide-react';
import { useMapStore } from '../../stores/mapStore';
import { useAuthStore } from '../../stores/authStore';
import { useVisibleRecentSearches } from '../../hooks/useVisibleRecentSearches';
import { getCategoryIconName, getSearchResultCategoryLabel, quickCategories } from '../../constants/categories';
import type { AutocompleteResult } from '../../services/api';
import { DynamicIcon, colorClasses } from '../ui/DynamicIcon';
import { SearchResultItem, EmptySearchResults, SearchResultsHelper } from './SearchResultItem';
import { NearbyTransportStopsSection } from './NearbyTransportStopsSection';

interface SearchSuggestionsProps {
  onClose: () => void;
  onGetDirections?: (placeId: string) => void;
  onNavigateToStop?: (name: string, coords: { lat: number; lng: number }) => void;
  onAddMissingPlace?: () => void;
}

export function SearchSuggestions({ onClose, onGetDirections, onNavigateToStop, onAddMissingPlace }: SearchSuggestionsProps) {
  const {
    searchQuery,
    searchResults,
    autocompleteSuggestions,
    autocompleteHasMore,
    isLoadingMore,
    isSearching,
    searchError,
    selectPlace,
    selectAutocompleteResult,
    loadMoreAutocomplete,
    performSearch,
    setSearchQuery,
  } = useMapStore();

  const { requireAuth, openAuthModal, isAuthenticated } = useAuthStore();
  const recentSearches = useVisibleRecentSearches();
  const nearbyStopsRef = useRef<HTMLDivElement>(null);

  const handleSelectResult = (placeId: string) => {
    selectPlace(placeId);
    onClose();
  };

  const handleSelectAutocomplete = (result: AutocompleteResult) => {
    selectAutocompleteResult(result);
    onClose();
  };

  // Get icon name for a type/category using comprehensive mapping
  const getIconName = (type: string, category?: string | null): string => {
    if (category) {
      const iconName = getCategoryIconName(category);
      if (iconName !== 'map-pin') return iconName;
    }
    return getCategoryIconName(type);
  };

  const handleRecentSearch = (query: string) => {
    setSearchQuery(query);
    performSearch(query);
  };

  const handleRecentDirections = (e: React.MouseEvent, placeId: string | undefined) => {
    e.stopPropagation();
    if (!placeId) return;

    requireAuth(() => {
      selectPlace(placeId);
      onClose();
      if (onGetDirections) {
        onGetDirections(placeId);
      }
    });
  };

  const handleCategorySearch = (category: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSearchQuery(category);
    performSearch(category);
  };

  // Show autocomplete suggestions or search results if we have a query
  if (searchQuery.trim()) {
    // Show autocomplete suggestions while typing (before pressing Enter)
    const hasAutocompleteSuggestions = autocompleteSuggestions.length > 0;
    const hasSearchResults = searchResults.length > 0;

    return (
      <div className="bg-white dark:bg-gray-900 overflow-hidden">
        {searchError ? (
          <div className="p-6 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <p className="text-gray-900 dark:text-white font-medium mb-1">Rate Limit Reached</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm">{searchError}</p>
            {!isAuthenticated ? (
              <button
                type="button"
                onClick={() => openAuthModal('login')}
                className="inline-block mt-3 px-4 py-2 bg-primary-500 text-white text-sm rounded-full hover:bg-primary-600 transition-colors"
              >
                Sign in for higher limits
              </button>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-xs mt-3">
                Signed in — limits refresh over time. Try again shortly.
              </p>
            )}
          </div>
        ) : isSearching && !hasAutocompleteSuggestions && !hasSearchResults ? (
          <div className="p-6 text-center">
            <div className="animate-spin w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full mx-auto" />
            <p className="mt-2 text-gray-500 dark:text-gray-400 text-sm">Searching...</p>
          </div>
        ) : hasAutocompleteSuggestions ? (
          // Autocomplete dropdown - premium unified styling
          <div>
            {autocompleteSuggestions.map((suggestion, index) => (
              <SearchResultItem
                key={`${suggestion.id || suggestion.text}-${index}`}
                name={suggestion.text}
                address={suggestion.address}
                district={suggestion.district}
                categoryLabel={getSearchResultCategoryLabel(suggestion.type, suggestion.category)}
                coordalPostcode={suggestion.postcode}
                coordalDisplay={suggestion.postcode_display}
                ghanaPostcode={suggestion.ghana_post}
                openingHoursStatus={suggestion.opening_hours_status}
                distanceMeters={suggestion.distance_meters}
                iconName={getIconName(suggestion.type, suggestion.category)}
                floodRiskLevel={(suggestion.type === 'street' || suggestion.type === 'address') ? suggestion.flood_risk_level : null}
                floodProne={(suggestion.type === 'street' || suggestion.type === 'address') ? suggestion.flood_prone : null}
                onClick={() => handleSelectAutocomplete(suggestion)}
                onDirectionsClick={() => {
                  requireAuth(() => {
                    handleSelectAutocomplete(suggestion);
                    if (onGetDirections && suggestion.id) {
                      onGetDirections(suggestion.id);
                    }
                  });
                }}
                compact={true}
                isPlace={suggestion.type === 'place' || suggestion.type === 'country'}
              />
            ))}
            {autocompleteHasMore && (
              <button
                onClick={loadMoreAutocomplete}
                disabled={isLoadingMore}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-800 border-t border-gray-100 dark:border-gray-700 transition-colors disabled:opacity-60"
              >
                {isLoadingMore ? (
                  <><Loader2 size={14} className="animate-spin" /> Loading...</>
                ) : (
                  <><ChevronDown size={14} /> Show more results</>
                )}
              </button>
            )}
            {!autocompleteHasMore && <SearchResultsHelper query={searchQuery} />}
          </div>
        ) : hasSearchResults ? (
          // Show full search results - premium unified styling
          <div>
            {searchResults.map((result) => (
              <SearchResultItem
                key={result.id}
                name={result.name}
                address={result.address}
                district={result.district}
                categoryLabel={getSearchResultCategoryLabel(result.type, result.category)}
                coordalPostcode={result.postcode}
                coordalDisplay={result.postcode_display}
                ghanaPostcode={result.ghana_post}
                openingHoursStatus={result.opening_hours_status}
                distanceMeters={result.distance_meters}
                iconName={getIconName('poi', result.category)}
                floodRiskLevel={(result.type === 'street' || result.type === 'address') ? result.flood_risk_level : null}
                floodProne={(result.type === 'street' || result.type === 'address') ? result.flood_prone : null}
                onClick={() => handleSelectResult(result.id)}
                onDirectionsClick={() => {
                  requireAuth(() => {
                    selectPlace(result.id);
                    onClose();
                    if (onGetDirections) {
                      onGetDirections(result.id);
                    }
                  });
                }}
                isPlace={result.type === 'place' || result.type === 'country'}
              />
            ))}
            <SearchResultsHelper query={searchQuery} />
          </div>
        ) : (
          <EmptySearchResults onAddMissingPlace={onAddMissingPlace} />
        )}
      </div>
    );
  }

  const handleTransportQuickSearch = (e: React.MouseEvent) => {
    handleCategorySearch('Transport', e);
  };

  // Show recent searches and quick categories when no query
  return (
    <div className="bg-white dark:bg-gray-900 overflow-hidden">
      {/* Quick Categories */}
      <div className="p-4 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Quick Search Near Me
          </p>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={handleTransportQuickSearch}
            className="flex flex-col items-center p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <div className="w-9 h-9 bg-emerald-500 rounded-xl flex items-center justify-center mb-1">
              <Bus size={18} className="text-white" />
            </div>
            <span className="text-xs text-gray-600 dark:text-gray-300">Transport</span>
          </button>
          {quickCategories.map((cat) => {
            const colors = colorClasses[cat.color] || colorClasses.gray;
            return (
              <button
                key={cat.id}
                onClick={(e) => handleCategorySearch(cat.label, e)}
                className="flex flex-col items-center p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <div className={`w-9 h-9 ${colors.bg} rounded-xl flex items-center justify-center mb-1`}>
                  <DynamicIcon name={cat.icon} size={18} className="text-white" />
                </div>
                <span className="text-xs text-gray-600 dark:text-gray-300">{cat.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <NearbyTransportStopsSection
        scrollRef={nearbyStopsRef}
        onStopSelect={onClose}
        onNavigateToStop={(name, coords) => {
          requireAuth(() => {
            onClose();
            onNavigateToStop?.(name, coords);
          });
        }}
      />

      {/* Recent Searches */}
      {recentSearches.length > 0 && (
        <div className="p-4">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Recent
          </p>
          <div className="space-y-1">
            {recentSearches.slice(0, 5).map((recent) => (
              <div
                key={recent.id}
                className="flex items-center gap-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors overflow-hidden"
              >
                <button
                  onClick={() => handleRecentSearch(recent.query)}
                  className="flex-1 flex items-center gap-3 p-2 text-left min-w-0"
                >
                  <Clock size={16} className="text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-gray-700 dark:text-gray-200 block truncate">{recent.query}</span>
                  </div>
                </button>
                {/* Navigation button for recent searches with results */}
                {recent.result && (
                  <button
                    onClick={(e) => handleRecentDirections(e, recent.result?.id)}
                    className="p-2 mr-1 text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-full transition-colors flex-shrink-0"
                    title="Get directions"
                  >
                    <Navigation size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
